import { Check, Database, LayoutTemplate, MousePointerClick, Palette, Sparkles } from "lucide-react";
import type { CSSProperties } from "react";

import { Button, Modal } from "../ds";
import { TEMPLATES, type ReportTemplate } from "../pages/v2/templates";
import type { PreviewTheme } from "../shell/TvFrameTheme";

/* What an empty page looks like in the designer: the TV grid faintly drawn,
 * one big drop zone, and the three ways to get started. Sits over the
 * renderer's stage so it replaces its bare "no widgets" line. */
export function EmptyCanvas({ style, gridSize, theme, dropping, onAddVisual, onSuggest, onTemplate, hasData, onPickData, onStyle }: { style: CSSProperties; gridSize: string; theme: PreviewTheme; dropping: boolean; onAddVisual: () => void; onSuggest: () => void; onTemplate: () => void; /** Guided "Start here" flow: is there data in the report yet? */ hasData?: boolean; onPickData?: () => void; onStyle?: () => void }) {
  const guided = !!onPickData;
  return (
    <div className={`st-empty st-empty--${theme}${dropping ? " is-dropping" : ""}`} style={style} onMouseDown={(e) => e.stopPropagation()}>
      <div className="st-empty__grid" style={{ backgroundSize: gridSize }} />
      <div className="st-empty__zone">
        <div className="st-empty__art" aria-hidden="true">
          <span style={{ gridArea: "a" }} />
          <span style={{ gridArea: "b" }} />
          <span style={{ gridArea: "c" }} />
          <span style={{ gridArea: "d" }} />
        </div>
        <h2 className="st-empty__title">{dropping ? "Drop it here" : guided ? "Start here - three easy steps" : "This page is empty — let’s build it"}</h2>
        <p className="st-empty__sub">{dropping ? "Let go and the visual lands where you point." : guided ? "No Power BI knowledge needed. Do them in order; the Guide button at the top keeps track." : "Drag a visual from the left onto this area, or pick a starting point:"}</p>
        {!dropping && guided ? (
          <div className="st-empty__actions">
            <button type="button" className={`st-empty__card${hasData ? " is-done" : " is-primary"}`} onClick={onPickData}>
              <span className="st-empty__icon">{hasData ? <Check size={20} /> : <Database size={20} />}</span>
              <span className="st-empty__ct">1. Pick your data</span>
              <span className="st-empty__cd">{hasData ? "Done. Add more any time from the Data tab." : "Choose the file or SharePoint site your numbers come from."}</span>
            </button>
            <button type="button" className={`st-empty__card${hasData ? " is-primary" : ""}`} onClick={onSuggest}>
              <span className="st-empty__icon"><Sparkles size={20} /></span>
              <span className="st-empty__ct">2. Add a visual</span>
              <span className="st-empty__cd">Let us suggest charts that fit your data - or drag a column from the Data tab onto this page and the right chart appears.</span>
            </button>
            <button type="button" className="st-empty__card" onClick={onStyle}>
              <span className="st-empty__icon"><Palette size={20} /></span>
              <span className="st-empty__ct">3. Style it</span>
              <span className="st-empty__cd">Pick a Look (colours and fonts) for the whole report. Then Preview and Publish from the top bar.</span>
            </button>
          </div>
        ) : null}
        {!dropping && guided ? (
          <p className="st-empty__sub st-empty__alt">
            Prefer another way? <button type="button" className="st-link" onClick={onAddVisual}>Choose a visual yourself</button> or <button type="button" className="st-link" onClick={onTemplate}>start from a template</button>.
          </p>
        ) : null}
        {!dropping && !guided ? (
          <div className="st-empty__actions">
            <button type="button" className="st-empty__card" onClick={onAddVisual}>
              <span className="st-empty__icon"><MousePointerClick size={20} /></span>
              <span className="st-empty__ct">Add a visual</span>
              <span className="st-empty__cd">Choose a chart, KPI, table or filter from the Visuals list.</span>
            </button>
            <button type="button" className="st-empty__card is-primary" onClick={onSuggest}>
              <span className="st-empty__icon"><Sparkles size={20} /></span>
              <span className="st-empty__ct">Suggest visuals from my data</span>
              <span className="st-empty__cd">Pick a dataset and get ready-made charts that fit its columns.</span>
            </button>
            <button type="button" className="st-empty__card" onClick={onTemplate}>
              <span className="st-empty__icon"><LayoutTemplate size={20} /></span>
              <span className="st-empty__ct">Start from a template</span>
              <span className="st-empty__cd">A finished layout (KPIs, trend, table). You just choose the data.</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Layout thumbnail of a template page on the 24-column grid. */
export function TemplateThumb({ t, accent }: { t: ReportTemplate; accent?: string }) {
  const ws = t.pages[0]?.widgets ?? [];
  return (
    <div className="st-tthumb" aria-hidden="true">
      {ws.map((w) => (
        <span
          key={w.id}
          className={`st-tthumb__w st-tthumb__w--${w.type}`}
          style={{ left: `${(w.x / 24) * 100}%`, top: `${(w.y / 33) * 100}%`, width: `calc(${(w.w / 24) * 100}% - 3px)`, height: `calc(${(w.h / 33) * 100}% - 3px)`, borderColor: accent ?? t.accent }}
        />
      ))}
    </div>
  );
}

export function TemplatePickerModal({ open, onClose, onPick, hasDataset }: { open: boolean; onClose: () => void; onPick: (t: ReportTemplate) => void; hasDataset: boolean }) {
  const list = TEMPLATES.filter((t) => t.id !== "blank" && t.pages[0]?.widgets.length);
  return (
    <Modal open={open} onClose={onClose} size="lg" title="Start this page from a template" description={hasDataset ? "The visuals are placed on this page, already pointed at the dataset you picked — then you choose the fields." : "The visuals are placed on this page ready for data — then pick a dataset and fields for each one."} footer={<Button onClick={onClose}>Cancel</Button>}>
      <div className="st-tpl-grid">
        {list.map((t) => (
          <button key={t.id} type="button" className="st-tpl" onClick={() => onPick(t)}>
            <TemplateThumb t={t} />
            <span className="st-tpl__name">{t.name}</span>
            <span className="st-tpl__desc">{t.description}</span>
            <span className="st-tpl__count">{t.pages[0].widgets.length} visuals{t.pages.length > 1 ? ` · ${t.pages.length} pages` : ""}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
